import React from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { Button } from './ui/Button';
import { Crown, Lock, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';

interface FeatureGateProps {
  feature: keyof import('../hooks/useSubscription').SubscriptionFeatures;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgrade?: boolean;
}

export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  children,
  fallback,
  showUpgrade = true,
}) => {
  const { subscription, hasFeature, loading } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showUpgrade) {
    return null;
  }

  const getRequiredPlan = () => {
    switch (feature) {
      case 'customTemplates':
      case 'advancedFormatting':
      case 'teamCollaboration':
        return 'Basic';
      case 'analytics':
      case 'realTimeEditing':
        return 'AI';
      case 'apiAccess':
      case 'whiteLabel':
        return 'Agent';
      default:
        return 'Premium';
    }
  };

  const requiredPlan = getRequiredPlan();

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-lg p-6 text-center">
      <div className="flex justify-center mb-4">
        {requiredPlan === 'Agent' ? (
          <Crown className="w-12 h-12 text-purple-500" />
        ) : requiredPlan === 'AI' ? (
          <Zap className="w-12 h-12 text-green-500" />
        ) : (
          <Lock className="w-12 h-12 text-blue-500" />
        )}
      </div>
      <h3 className="text-lg font-bold mb-2">{t('featureGate.premiumTitle')}</h3>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        {t('featureGate.premiumDesc', { plan: requiredPlan })}
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {t('featureGate.currentPlan', { plan: subscription?.plan || t('featureGate.planFree') })}
      </p>
      <Button
        onClick={() => navigate('/pricing')}
        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
      >
        <Crown className="w-4 h-4 mr-2" />
        {t('featureGate.upgradeNow')}
      </Button>
    </div>
  );
};

interface AIFeatureGateProps {
  requiredLevel: 'basic' | 'advanced' | 'premium';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const AIFeatureGate: React.FC<AIFeatureGateProps> = ({
  requiredLevel,
  children,
  fallback,
}) => {
  const { canUseAIFeature, loading } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (canUseAIFeature(requiredLevel)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  const getRequiredPlan = () => {
    switch (requiredLevel) {
      case 'advanced':
        return 'AI';
      case 'premium':
        return 'Agent';
      default:
        return 'Basic';
    }
  };

  const requiredPlan = getRequiredPlan();

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-dashed border-purple-300 dark:border-purple-600 rounded-lg p-4 text-center">
      <Zap className="w-8 h-8 text-purple-500 mx-auto mb-2" />
      <h4 className="font-bold mb-1">{t('featureGate.advancedAITitle')}</h4>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
        {t('featureGate.advancedAIDesc', { plan: requiredPlan, level: requiredLevel })}
      </p>
      <Button
        size="sm"
        onClick={() => navigate('/pricing')}
        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
      >
        {t('featureGate.upgrade')}
      </Button>
    </div>
  );
};